import { FC, useEffect, useRef, useState } from 'react'
import { TransitionGroup, Transition as ReactTransition } from 'react-transition-group'

import Header from 'components/ui/header'

import { useRouter } from 'next/router'

import { unhideChatButton } from '../../../utils/hide-chat-button'
import FirstLoanBanner from 'components/ui/firt-loan-banner'
import { useAppSelector } from 'store/hooks'
import { LoginStatuses, selectedAuthStatus, selectedUser } from 'store/reducers/users'
import useLocationSearch from 'libs/location-search'
import { fetchReferral } from 'store/fetchers/utils'
import { safeSessionStorage } from 'utils/safe-session-storage'

export const Transition: FC<{
  location: string
  code: string
}> = ({ children, location, code = '' }) => {
  const isVipPage = location.includes('private-clients')
  const authStatus = useAppSelector(selectedAuthStatus)
  const user = useAppSelector(selectedUser)
  const query = useLocationSearch()
  const referralId = Array.isArray(query.referral) ? query.referral[0] : query.referral

  const [discount, setDiscount] = useState(null)

  const isRedesigned = (location === '/' ||
    location === '/exchange' ||
    location === '/earn' ||
    location === '/loans/[coin]' ||
    location === '/coin/[coin]'
  )

  const isMainPage = location === '/'

  const iframeObserverRef = useRef<MutationObserver | null>(null)
  const router = useRouter()

  useEffect(() => {
    const observerCallback = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const iframe = document.getElementById('launcher') as HTMLIFrameElement
          if (iframe && typeof window !== 'undefined' && window.innerWidth < 765) {
            unhideChatButton()
            break // Stop observing after finding the iframe
          }
        }
      }
    }

    // Create a MutationObserver to watch for changes in the DOM
    const observer = new MutationObserver(observerCallback)
    iframeObserverRef.current = observer

    // Start observing the document body for added or removed child elements
    observer.observe(document.body, { childList: true, subtree: true })

    // Clean up the observer on component unmount
    return () => {
      if (iframeObserverRef.current) {
        iframeObserverRef.current.disconnect()
      }
    }
  }, [router.pathname]) // Re-run effect on pathname change

  const validateReferral = async () => {
    try {
      const { loan_discount, ltv_discounts } = await fetchReferral({ referral_code: referralId })

      if (!loan_discount || !ltv_discounts) return

      const entries = Object.entries(ltv_discounts)
      const [, maxDiscount] = entries?.length ? entries?.at(-1) : [undefined, undefined]

      if (!isNaN(Number(loan_discount)) && loan_discount !== '0') {
        safeSessionStorage.setItem('loanCalculator:firstLoanDiscount', loan_discount)
      }

      if (!isNaN(Number(maxDiscount)) && maxDiscount !== '0') {
        setDiscount(Math.abs(+maxDiscount)?.toString())
      }
    } catch (error) {
      safeSessionStorage.removeItem('loanCalculator:firstLoanDiscount')
    }
  }

  useEffect(() => {
    if (user) {
      localStorage?.removeItem('referral')
    }

    if (user || isMainPage && !referralId) {
      safeSessionStorage.removeItem('loanCalculator:firstLoanDiscount')
      return
    }

    if (!isMainPage || !referralId || !authStatus || user) {
      return
    }

    validateReferral()
  }, [authStatus, referralId, user])

  switch (code.toLowerCase()) {
    case 'usdttrc20':
      code = 'usdt'
      break
  }

  return (
    <>
      {
        (authStatus === LoginStatuses.LOGOUT || authStatus === LoginStatuses.INIT)
        && isMainPage && referralId && discount && <FirstLoanBanner discount={discount} />
      }

      <Header
        type={isVipPage ? 'tretiary' : 'primary'}
        isMainPage={isRedesigned}
      />

      <TransitionGroup
        style={{
          position: 'relative',
          overflow: isRedesigned ? 'visible' : 'hidden',
        }}
      >
        <ReactTransition
          key={location}
          timeout={{
            enter: 0,
            exit: 0,
          }}
        >
          {() => <div>{children}</div>}
        </ReactTransition>
      </TransitionGroup>
    </>
  )
}
